import React, { useState, useMemo, useEffect } from 'react';
import { Container, Row, Col, ListGroup, Card, Badge, Form, InputGroup, Button, Dropdown } from 'react-bootstrap';
import { FaUser, FaRobot, FaComments, FaSearch, FaGlobe, FaPaperPlane, FaDownload, FaCheck, FaExclamationTriangle, FaBookmark } from 'react-icons/fa';
import { SiWhatsapp } from 'react-icons/si';
import CustomPagination from '../Other/CutomPagination';
import { useParams } from 'react-router-dom';
import { useAgentContext } from '../../context/agent';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { getConversations, updatedConversation} from '../../api/agent';
import { IConversation, IMessage } from '../../models';

import './style.css';
import AddFAQ from '../Knowledgebase/AddFAQ';

export default function ConversationDashboard() {
    const [count, setCount] = useState(0);
    const [page, setpage] = useState(1);
    const [limit, setLimit] = useState(10);

    const [addFAQ, setAddFAQ] = useState(false);
    const [qn, setQn] = useState("");

    const [searchQuery, setSearchQuery] = useState("");
    const [typeFilter, setTypeFilter] = useState<"all" | "web" | "whatsapp">(
        "all",
    );
    const [statusFilter, setStatusFilter] = useState<"all" | "active" | "pending" | "escalated" | "resolved">(
        "all",
    );

    const [newMessage, setNewMessage] = useState("");
    const [newStatus, setNewStatus] = useState<string>("");

    const [selectedConversationId, setSelectedConversationId] = useState<
        string | null
    >(null);
    const [conversations, setConversations] = useState<IConversation[]>([]);
    const selectedConversation = conversations.find(
        (conv) => conv.id === selectedConversationId,
    );

    const filteredConversations = useMemo(() => {
        return conversations.filter((conv) => {
            const matchesSearch =
                !searchQuery ||
                conv.sessionId
                    .toLowerCase()
                    .includes(searchQuery.toLowerCase()) ||
                conv.sender?.toLowerCase().includes(searchQuery.toLowerCase());
            // conv.lastMessage?.toLowerCase().includes(searchQuery.toLowerCase());

            const matchesType =
                typeFilter === "all" || conv.type === typeFilter;

            const matchesStatus =
                statusFilter === "all" || conv.status === statusFilter;

            return matchesSearch && matchesType && matchesStatus;
        });
    }, [conversations, searchQuery, typeFilter, statusFilter]);

    const getRoleIcon = (role: string) => {
        switch (role) {
            case "user":
                return <FaUser className="me-2 text-primary" />;
            case "assistant":
                return <FaRobot className="me-2 text-success" />;
            default:
                return null;
        }
    };

    const getStatusVariant = (status: string) => {
        switch (status) {
            case "active":
                return "success";
            case "pending":
                return "warning";
            case "escalated":
                return "danger";
            case "resolved":
                return "secondary";
            default:
                return "light";
        }
    };

    const getTypeIcon = (type: string) => {
        return type === "web" ? (
            <FaGlobe className="me-1" />
        ) : (
            <SiWhatsapp className="me-1" />
        );
    };

    const handleSendMessage = () => {
        if (!newMessage.trim() || !selectedConversation) return;

        // const newMsg: IMessage = {
        // id: `msg_${Date.now()}`,
        // role: 'assistant',
        // name: null,
        // content: newMessage.trim(),
        // conversationId: selectedConversation.id,
        // agentId: selectedConversation.agentId,
        // createdAt: new Date()
        // };

        // setConversations(prev => prev.map(conv =>
        // conv.id === selectedConversation.id
        //     ? {
        //         ...conv,
        //         messages: [...conv.messages, newMsg],
        //         messageCount: conv.messageCount + 1,
        //         lastMessage: newMsg.content,
        //         lastActivity: new Date(),
        //         updatedAt: new Date()
        //     }
        //     : conv
        // ));

        setNewMessage("");
    };

    const handleExport = () => {
        const csvContent = conversations
            .map(
                (conv) =>
                    `"${conv.id}","${conv.sessionId}","${conv.type}","${conv.sender || "Anonymous"}","${conv.agentId},"${conv.createdAt}"`,
            )
            .join("\n");

        const csvHeader =
            "ID,Session ID,Type,Sender,Agent ID,Status,Message Count,Created At,Last Activity\n";
        const fullCsv = csvHeader + csvContent;

        const blob = new Blob([fullCsv], { type: "text/csv" });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = "conversations.csv";
        a.click();
        window.URL.revokeObjectURL(url);
    };

    const handleAddToKnowledgebase = (message: IMessage) => {
        setQn(message.content);
        setAddFAQ(true);
    };

    const formatTime = (date: Date) => {
        return date.toLocaleTimeString("en-US", {
            hour: "numeric",
            minute: "2-digit",
            hour12: true,
        });
    };

    const formatDate = (date: Date) => {
        const now = new Date();
        const diffTime = Math.abs(now.getTime() - date.getTime());
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

        if (diffDays === 1) return "Today";
        if (diffDays === 2) return "Yesterday";
        if (diffDays <= 7) return `${diffDays - 1} days ago`;
        return date.toLocaleDateString();
    };

    // Filter out system messages
    const getFilteredMessages = (messages: IMessage[]) => {
        return messages.filter((msg) => msg.role !== "system");
    };

    const { agentId } = useParams();
    const { getAgentById } = useAgentContext();
    const agent = getAgentById(agentId || "");

    const { data } = useQuery({
        queryKey: ["Conversations", agent?.accessKey],
        queryFn: () =>
            getConversations({
                orgId: agent?.organizationId!,
                agentId: agent?.id!,
                limit: 100,
                page,
            }),
        enabled: !!agent,
    });

    // update conversation useMutation (patch request)
    const queryClient = useQueryClient();
    const { mutateAsync: updateConversationFn, isLoading: isUpdating } =
        useMutation(updatedConversation, {
            onSuccess: (data) => {
                queryClient.invalidateQueries([
                    "Conversations",
                    agent?.accessKey,
                ]);
            },
            onError: (error) => {
                console.error("Error updating conversation:", error);
            },
        });

    const handleStatusChange = () => {
        if (!selectedConversation || !newStatus || !agent) return;

        // setConversations(prev => prev.map(conv =>
        // conv.id === selectedConversation.id
        //     ? { ...conv, status: newStatus as any, updatedAt: new Date() }
        //     : conv
        // ));

        updateConversationFn({
            orgId: agent.organizationId,
            agentId: agent.id,
            conversationId: selectedConversation.id,
            status: newStatus as any,
        });

        setNewStatus("");
    };

    useEffect(() => {
        if (data) {
            setConversations(data.rows);
            setCount(data.count);
            // console.log(data.rows);
        }
    }, [data]);

    useEffect(() => {
        if (
            conversations &&
            conversations.length > 0 &&
            !selectedConversationId
        ) {
            setSelectedConversationId(conversations[0].id);
        }
    }, [conversations]);

    return (
        <div
            style={{
                backgroundColor: "#1a1a1a",
                color: "#ffffff",
                minHeight: "60vh",
            }}
        >
            {addFAQ && <AddFAQ show={addFAQ} setShow={setAddFAQ} qn={qn} />}

            {/* Header */}
            <div
                style={{
                    backgroundColor: "#2d2d2d",
                    borderBottom: "1px solid #444",
                }}
                className="sticky-top"
            >
                <Container fluid className="py-3">
                    <Row className="align-items-center">
                        <Col>
                            <div className="d-flex align-items-center">
                                <div className="bg-primary rounded p-2 me-3">
                                    <FaComments className="text-white" />
                                </div>
                                <div>
                                    <h4 className="mb-0 text-light">
                                        Conversations
                                    </h4>
                                    <small style={{ color: "#999" }}>
                                        {conversations.length} conversations
                                    </small>
                                </div>
                            </div>
                        </Col>
                        <Col xs="auto">
                            <Button
                                variant="outline-light"
                                size="sm"
                                onClick={handleExport}
                            >
                                <FaDownload className="me-2" />
                                Export
                            </Button>
                        </Col>
                    </Row>
                </Container>
            </div>

            <Container fluid className="h-100">
                <Row style={{ height: "calc(100vh - 80px)" }}>
                    {/* Sidebar */}
                    <Col
                        md={3}
                        style={{
                            backgroundColor: "#2d2d2d",
                            borderRight: "1px solid #444",
                            height: "100%",
                            overflowY: "auto",
                        }}
                    >
                        <div
                            className="p-3"
                            style={{ borderBottom: "1px solid #444" }}
                        >
                            <h5 className="text-light mb-3">
                                <FaComments className="me-2" />
                                Conversations
                            </h5>

                            {/* Search */}
                            <InputGroup className="mb-3">
                                <InputGroup.Text
                                    style={{
                                        backgroundColor: "#444",
                                        borderColor: "#555",
                                        color: "#ccc",
                                    }}
                                >
                                    <FaSearch />
                                </InputGroup.Text>
                                <Form.Control
                                    type="text"
                                    placeholder="Search conversations..."
                                    value={searchQuery}
                                    onChange={(e) =>
                                        setSearchQuery(e.target.value)
                                    }
                                    style={{
                                        backgroundColor: "#444",
                                        borderColor: "#555",
                                        color: "#fff",
                                    }}
                                    className="custom-placeholder"
                                />
                            </InputGroup>

                            {/* Type Filter */}
                            <Form.Select
                                size="sm"
                                value={typeFilter}
                                onChange={(e) =>
                                    setTypeFilter(e.target.value as any)
                                }
                                style={{
                                    backgroundColor: "#444",
                                    borderColor: "#555",
                                    color: "#fff",
                                }}
                                className="mb-2"
                            >
                                <option value="all">All Types</option>
                                <option value="web">Web Chat</option>
                                <option value="whatsapp">WhatsApp</option>
                            </Form.Select>

                            {/* Status Filter */}
                            <Form.Select
                                size="sm"
                                value={statusFilter}
                                onChange={(e) =>
                                    setStatusFilter(e.target.value as any)
                                }
                                style={{
                                    backgroundColor: "#444",
                                    borderColor: "#555",
                                    color: "#fff",
                                }}
                            >
                                <option value="all">All Statuses</option>
                                <option value="active">Active</option>
                                <option value="pending">Pending</option>
                                <option value="escalated">Escalated</option>
                                <option value="resolved">Resolved</option>
                            </Form.Select>
                        </div>

                        {/* Conversations List */}
                        <ListGroup variant="flush">
                            {filteredConversations.length === 0 ? (
                                <div className="text-center py-5">
                                    <FaComments
                                        className="text-light mb-3"
                                        size={48}
                                    />
                                    <p className="text-light">
                                        No conversations found
                                    </p>
                                </div>
                            ) : (
                                filteredConversations.map((conversation) => (
                                    <ListGroup.Item
                                        key={conversation.id}
                                        action
                                        active={
                                            selectedConversationId ===
                                            conversation.id
                                        }
                                        onClick={() =>
                                            setSelectedConversationId(
                                                conversation.id,
                                            )
                                        }
                                        style={{
                                            backgroundColor:
                                                selectedConversationId ===
                                                conversation.id
                                                    ? "#444"
                                                    : "#2d2d2d",
                                            borderColor: "#444",
                                            color: "#fff",
                                            borderLeft:
                                                selectedConversationId ===
                                                conversation.id
                                                    ? "4px solid #007bff"
                                                    : "none",
                                        }}
                                    >
                                        <div className="d-flex justify-content-between align-items-start mb-2">
                                            <div className="d-flex align-items-center">
                                                <Badge
                                                    bg={
                                                        conversation.type ===
                                                        "web"
                                                            ? "primary"
                                                            : "success"
                                                    }
                                                    className="me-2"
                                                >
                                                    {getTypeIcon(
                                                        conversation.type,
                                                    )}
                                                    {conversation.type.toUpperCase()}
                                                </Badge>
                                                <Badge
                                                    bg={getStatusVariant(
                                                        conversation.status,
                                                    )}
                                                    className="me-2"
                                                >
                                                    {conversation.status ===
                                                        "resolved" && (
                                                        <FaCheck className="me-1" />
                                                    )}
                                                    {conversation.status ===
                                                        "escalated" && (
                                                        <FaExclamationTriangle className="me-1" />
                                                    )}
                                                    {conversation.status}
                                                </Badge>
                                            </div>
                                            {/* <small style={{ color: '#999' }}>{formatDate(conversation.lastActivity!)}</small> */}
                                        </div>

                                        <div className="mb-2">
                                            <div className="fw-bold">
                                                Session on{" "}
                                                {new Date(
                                                    conversation.updatedAt,
                                                ).toLocaleDateString()}
                                            </div>
                                            <small style={{ color: "#ccc" }}>
                                                {/* {conversation.sender || 'Anonymous'} */}
                                                {[
                                                    conversation.sender,
                                                    conversation.senderEmail,
                                                    conversation.senderPhoneNumber,
                                                ]
                                                    .filter(Boolean)
                                                    .join(" • ") || "Anonymous"}
                                            </small>
                                        </div>

                                        <div
                                            style={{ color: "#999" }}
                                            className="small mb-2 text-truncate"
                                        >
                                            {/* {conversation.lastMessage || 'No messages'} */}
                                        </div>

                                        <div className="d-flex justify-content-between align-items-center">
                                            <small style={{ color: "#999" }}>
                                                {conversation.messages.length}{" "}
                                                messages
                                            </small>
                                            {/* <small className="text-info fw-bold">{conversation.agentId}</small> */}
                                        </div>
                                    </ListGroup.Item>
                                ))
                            )}
                        </ListGroup>

                        <CustomPagination
                            totalCount={count}
                            limit={limit}
                            currentPage={page}
                            setPage={(page) => setpage(page)}
                            setLimit={(limit) => setLimit(limit)}
                        />
                    </Col>

                    {/* Main Content */}
                    <Col md={9} className="p-0 d-flex flex-column">
                        {selectedConversation ? (
                            <>
                                {/* Conversation Header */}
                                <div
                                    className="p-3"
                                    style={{
                                        borderBottom: "1px solid #444",
                                        backgroundColor: "#2d2d2d",
                                    }}
                                >
                                    <Row className="align-items-center">
                                        <Col>
                                            <Row className="mb-2">
                                                <Col md={8} xs="auto">
                                                    <h5 className="mb-1 text-light">
                                                        Conversation Details –{" "}
                                                        {formatDate(
                                                            new Date(
                                                                selectedConversation.updatedAt,
                                                            ),
                                                        )}
                                                        ,{" "}
                                                        {formatTime(
                                                            new Date(
                                                                selectedConversation.updatedAt,
                                                            ),
                                                        )}{" "}
                                                    </h5>
                                                </Col>
                                                <Col md={4} xs="auto">
                                                    <div className="d-flex justify-content-end align-items-center gap-2">
                                                        <Form.Select
                                                            size="sm"
                                                            value={newStatus}
                                                            onChange={(e) =>
                                                                setNewStatus(
                                                                    e.target
                                                                        .value,
                                                                )
                                                            }
                                                            style={{
                                                                backgroundColor:
                                                                    "#444",
                                                                borderColor:
                                                                    "#555",
                                                                color: "#fff",
                                                                width: "auto",
                                                            }}
                                                        >
                                                            <option value="">
                                                                Change Status
                                                            </option>
                                                            <option value="active">
                                                                Active
                                                            </option>
                                                            <option value="pending">
                                                                Pending
                                                            </option>
                                                            <option value="escalated">
                                                                Escalated
                                                            </option>
                                                            <option value="resolved">
                                                                Resolved
                                                            </option>
                                                        </Form.Select>
                                                        {newStatus &&
                                                            newStatus !==
                                                                selectedConversation.status && (
                                                                <Button
                                                                    size="sm"
                                                                    variant="primary"
                                                                    onClick={
                                                                        handleStatusChange
                                                                    }
                                                                >
                                                                    Update
                                                                </Button>
                                                            )}
                                                    </div>
                                                </Col>
                                            </Row>

                                            <div className="row">
                                                <div className="col-md-6">
                                                    <div>
                                                        <Badge
                                                            bg="info"
                                                            className="me-2"
                                                        >
                                                            Type
                                                        </Badge>{" "}
                                                        {
                                                            selectedConversation.type
                                                        }
                                                    </div>
                                                    <div>
                                                        <Badge
                                                            bg="secondary"
                                                            className="me-2"
                                                        >
                                                            Sender
                                                        </Badge>
                                                        {[
                                                            selectedConversation.sender,
                                                            selectedConversation.senderEmail,
                                                            selectedConversation.senderPhoneNumber,
                                                        ]
                                                            .filter(Boolean)
                                                            .join(" • ") ||
                                                            "Anonymous"}
                                                    </div>
                                                    <div>
                                                        <Badge
                                                            bg={getStatusVariant(
                                                                selectedConversation.status,
                                                            )}
                                                            className="me-2"
                                                        >
                                                            Status
                                                        </Badge>{" "}
                                                        {
                                                            selectedConversation.status
                                                        }
                                                    </div>
                                                </div>
                                                <div className="col-md-6">
                                                    <div>
                                                        <Badge
                                                            bg="success"
                                                            className="me-2"
                                                        >
                                                            Access Key
                                                        </Badge>{" "}
                                                        {
                                                            selectedConversation.accessKey
                                                        }
                                                    </div>
                                                    <div>
                                                        <Badge
                                                            bg="warning"
                                                            className="me-2 text-dark"
                                                        >
                                                            Agent ID
                                                        </Badge>{" "}
                                                        {
                                                            selectedConversation.agentId
                                                        }
                                                    </div>
                                                    {/* <div><Badge bg="dark" className="me-2">Session</Badge> {selectedConversation.sessionId}</div> */}
                                                </div>
                                            </div>
                                        </Col>
                                    </Row>
                                </div>

                                {/* Messages Area */}
                                <div
                                    className="flex-fill overflow-auto p-3"
                                    style={{
                                        height: "50vh",
                                        overflowY: "auto",
                                        // maxHeight: "600px"
                                    }}
                                >
                                    {getFilteredMessages(
                                        selectedConversation.messages,
                                    ).length === 0 ? (
                                        <div className="d-flex justify-content-center align-items-center h-100">
                                            <div className="text-center">
                                                <FaComments
                                                    className="text-light mb-3"
                                                    size={48}
                                                />
                                                <p className="text-light">
                                                    No messages in this
                                                    conversation
                                                </p>
                                            </div>
                                        </div>
                                    ) : (
                                        getFilteredMessages(
                                            selectedConversation.messages,
                                        ).map((message: IMessage) => {
                                            const isUser =
                                                message.role === "user";
                                            return (
                                                <Card
                                                    key={message.id}
                                                    className="mb-2"
                                                    style={{
                                                        backgroundColor:
                                                            "#2d2d2d",
                                                        borderColor: "#444",
                                                    }}
                                                >
                                                    <Card.Body>
                                                        <div className="d-flex align-items-center justify-content-between mb-2">
                                                            <div className="d-flex align-items-center">
                                                                {getRoleIcon(
                                                                    message.role,
                                                                )}
                                                                <strong className="me-2 text-capitalize text-light">
                                                                    {
                                                                        message.role
                                                                    }
                                                                </strong>
                                                                <small
                                                                    style={{
                                                                        color: "#ccc",
                                                                    }}
                                                                >
                                                                    {message.name ||
                                                                        "Anonymous"}
                                                                </small>
                                                            </div>
                                                            {isUser && (
                                                                <Dropdown>
                                                                    <Dropdown.Toggle
                                                                        variant="link"
                                                                        size="sm"
                                                                        style={{
                                                                            color: "#ccc",
                                                                            textDecoration:
                                                                                "none",
                                                                        }}
                                                                    >
                                                                        <FaBookmark />
                                                                    </Dropdown.Toggle>
                                                                    <Dropdown.Menu
                                                                        style={{
                                                                            backgroundColor:
                                                                                "#444",
                                                                            borderColor:
                                                                                "#555",
                                                                        }}
                                                                    >
                                                                        <Dropdown.Item
                                                                            onClick={() =>
                                                                                handleAddToKnowledgebase(
                                                                                    message,
                                                                                )
                                                                            }
                                                                            style={{
                                                                                color: "#fff",
                                                                            }}
                                                                            className="custom-dropdown-item"
                                                                        >
                                                                            Add
                                                                            to
                                                                            Knowledgebase
                                                                        </Dropdown.Item>
                                                                    </Dropdown.Menu>
                                                                </Dropdown>
                                                            )}
                                                        </div>
                                                        <Card.Text
                                                            style={{
                                                                color: "#fff",
                                                            }}
                                                        >
                                                            {message.content}
                                                        </Card.Text>
                                                        <small
                                                            style={{
                                                                color: "#999",
                                                            }}
                                                        >
                                                            {formatDate(
                                                                new Date(
                                                                    message.updatedAt,
                                                                ),
                                                            )}
                                                            ,{" "}
                                                            {formatTime(
                                                                new Date(
                                                                    message.updatedAt,
                                                                ),
                                                            )}
                                                        </small>
                                                    </Card.Body>
                                                </Card>
                                            );
                                        })
                                    )}
                                </div>

                                {/* Message Input */}
                                {selectedConversation.type === "whatsapp" &&
                                    selectedConversation.sender && (
                                        <div
                                            className="p-3"
                                            style={{
                                                borderTop: "1px solid #444",
                                                backgroundColor: "#2d2d2d",
                                            }}
                                        >
                                            <InputGroup>
                                                <Form.Control
                                                    as="textarea"
                                                    rows={1}
                                                    value={newMessage}
                                                    onChange={(e) =>
                                                        setNewMessage(
                                                            e.target.value,
                                                        )
                                                    }
                                                    placeholder="Type a message..."
                                                    style={{
                                                        backgroundColor: "#444",
                                                        borderColor: "#555",
                                                        color: "#fff",
                                                        resize: "none",
                                                    }}
                                                    onKeyDown={(e) => {
                                                        if (
                                                            e.key === "Enter" &&
                                                            !e.shiftKey
                                                        ) {
                                                            e.preventDefault();
                                                            handleSendMessage();
                                                        }
                                                    }}
                                                />
                                                <Button
                                                    variant="primary"
                                                    onClick={handleSendMessage}
                                                    disabled={
                                                        !newMessage.trim()
                                                    }
                                                >
                                                    <FaPaperPlane />
                                                </Button>
                                            </InputGroup>
                                        </div>
                                    )}

                                {selectedConversation.status !== "active" && (
                                    <div
                                        className="p-3 text-center"
                                        style={{
                                            borderTop: "1px solid #444",
                                            backgroundColor: "#444",
                                        }}
                                    >
                                        <Badge
                                            bg={getStatusVariant(
                                                selectedConversation.status,
                                            )}
                                            className="me-2"
                                        >
                                            {selectedConversation.status ===
                                                "resolved" && (
                                                <FaCheck className="me-1" />
                                            )}
                                            {selectedConversation.status ===
                                                "escalated" && (
                                                <FaExclamationTriangle className="me-1" />
                                            )}
                                            {selectedConversation.status
                                                .charAt(0)
                                                .toUpperCase() +
                                                selectedConversation.status.slice(
                                                    1,
                                                )}
                                        </Badge>
                                        <span style={{ color: "#ccc" }}>
                                            This conversation is{" "}
                                            {selectedConversation.status}
                                        </span>
                                    </div>
                                )}
                            </>
                        ) : (
                            <div className="d-flex justify-content-center align-items-center h-100">
                                <div className="text-center">
                                    <FaComments
                                        className="text-light mb-3"
                                        size={64}
                                    />
                                    <h5 className="text-light mb-2">
                                        Select a conversation
                                    </h5>
                                    <p className="text-light">
                                        Choose a conversation from the sidebar
                                        to view its messages.
                                    </p>
                                </div>
                            </div>
                        )}
                    </Col>
                </Row>
            </Container>
        </div>
    );
}
